import { Draft } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { Nullable } from 'clickNgo/models/utils'
import { getWTZDate } from 'core/classes/helpers/DateHelper'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IFetchQbrDocumentResponse } from '../../../models/qbr/IFetchQbrDocumentResponse'
import { IParsedSettingsFromQBRDocumentsListItem } from '../../../models/qbr/IParsedSettingsFromQBRDocumentsListItem'
import { defaultDocument, defaultDocumentSettings, initialState } from '../configs'
import { mapFromPeriodOption } from '../helpers/mapFromPeriodOption'
import { mapFromSelectedPeriod } from '../helpers/mapFromSelectedPeriod'
import { IQBRState } from '../models/state'

export const openQbrDocumentReducer = (
  state: Draft<IQBRState>,
  qbrDocument: IFetchQbrDocumentResponse,
  shouldKeepLastStepUntouched: boolean = false
) => {
  const isLocalDraft = qbrDocument.EntryId === 0

  state.isSelectedDocumentChanged = isLocalDraft

  state.selectedDocument = qbrDocument.Document
    ? { ...defaultDocument, ...JSON.parse(qbrDocument.Document) }
    : null

  state.pptxReportState.pptxReport = qbrDocument.PPTXReport
  state.pptxReportState.uid = qbrDocument.UID
  state.lastModified = dayjs(getWTZDate(qbrDocument.Modified)).toDate()
  state.fYear = qbrDocument.FYear || initialState.fYear
  state.fQuarter = qbrDocument.FQuarter || initialState.fQuarter
  state.name = qbrDocument.Name || initialState.name
  state.isLegacyDocument = qbrDocument.IsLegacyDocument === 1
  state.entryId = isLocalDraft ? null : qbrDocument.EntryId
  state.dnaLoadDate = qbrDocument.DNALoadDate
  state.rtm = qbrDocument.RTM || initialState.rtm

  state.globalPartnerId = qbrDocument.GlobalPartnerId || initialState.globalPartnerId

  if (!shouldKeepLastStepUntouched) {
    state.lastStep = qbrDocument.LastStep || initialState.lastStep
  }
  state.isFinalized = Boolean(qbrDocument.Finalized)

  const settings: Nullable<IParsedSettingsFromQBRDocumentsListItem> = qbrDocument.Settings
    ? JSON.parse(qbrDocument.Settings)
    : null

  if (settings) {
    state.documentSettings.netNdp =
      (settings.SelectedNetNdp as ActualsValueTypes) || defaultDocumentSettings.netNdp
    state.documentSettings.currency = settings.SelectedCurrency || defaultDocumentSettings.currency
    state.documentSettings.rounding = settings.SelectedRounding || defaultDocumentSettings.rounding
    state.documentSettings.currencyOptions =
      settings.Currency || defaultDocumentSettings.currencyOptions
    state.documentSettings.netNdpOptions = settings.NetNdp || defaultDocumentSettings.netNdpOptions
    state.documentSettings.offerings =
      settings.SelectedOfferings || defaultDocumentSettings.offerings
    state.documentSettings.selectedPeriod = settings.Quarters
      ? mapFromSelectedPeriod(settings.Quarters.Selected[0])
      : defaultDocumentSettings.selectedPeriod
    state.documentSettings.isGlobalPlans =
      settings.IsGlobalPlans ?? defaultDocumentSettings.isGlobalPlans

    const quarters = settings.Quarters
    if (quarters) {
      state.documentSettings.periodOptions = quarters.Available.map(mapFromPeriodOption)
      state.documentSettings.currentFYear = quarters.Current.FYear
      state.documentSettings.currentFQuarter = quarters.Current.FQuarter
    }

    state.documentSettings.exportSlides =
      settings.ExportSlides ?? defaultDocumentSettings.exportSlides
    state.documentSettings.eltVersion = settings.ELTVersion ?? defaultDocumentSettings.eltVersion
  }
}
